import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { flushSync } from 'react-dom'
import type { VisibilityState } from '@tanstack/react-table'
import type { FilterParams } from '../components/ListView/filterConfig'
import {
  getColumnVisibility,
  setColumnVisibility,
  getFilterParams,
  setFilterParams,
} from '../utils/listViewStorage'

export interface ListViewStorageApi {
  getColumnVisibility: typeof getColumnVisibility
  setColumnVisibility: typeof setColumnVisibility
  getFilterParams: typeof getFilterParams
  setFilterParams: typeof setFilterParams
}

const defaultStorage: ListViewStorageApi = {
  getColumnVisibility,
  setColumnVisibility,
  getFilterParams,
  setFilterParams,
}

export const ListViewStorageContext =
  createContext<ListViewStorageApi>(defaultStorage)

interface ListViewStorageProviderProps {
  children: ReactNode
  storage?: ListViewStorageApi
}

export function ListViewStorageProvider({
  children,
  storage = defaultStorage,
}: ListViewStorageProviderProps) {
  return (
    <ListViewStorageContext.Provider value={storage}>
      {children}
    </ListViewStorageContext.Provider>
  )
}

export function useListViewStorage(
  type: string | undefined,
  columnDefaultVisibility: Record<string, boolean> | undefined
) {
  const storage = useContext(ListViewStorageContext)
  const defaults = columnDefaultVisibility ?? {}

  const [columnVisibility, _setColumnVisibility] = useState<VisibilityState>(
    () => (type ? storage.getColumnVisibility(type, defaults) : defaults)
  )

  useEffect(() => {
    if (!type) {
      _setColumnVisibility(columnDefaultVisibility ?? {})
      return
    }
    _setColumnVisibility(
      storage.getColumnVisibility(type, columnDefaultVisibility ?? {})
    )
  }, [type, columnDefaultVisibility, storage])

  useEffect(() => {
    if (!type || !columnVisibility || Object.keys(columnVisibility).length === 0)
      return
    storage.setColumnVisibility(type, columnVisibility as Record<string, boolean>)
  }, [type, columnVisibility, storage])

  const [filterState, setFilterState] = useState<{
    type?: string
    params: FilterParams | null
  }>(() => ({
    type,
    params: type ? storage.getFilterParams(type) : null,
  }))

  // Sync from storage when type or storage changes (e.g. navigation to another list)
  useEffect(() => {
    if (type) {
      setFilterState({
        type,
        params: storage.getFilterParams(type),
      })
    } else {
      setFilterState({ type: undefined, params: null })
    }
  }, [type, storage])

  const saveFilterParams = useCallback(
    (params: FilterParams) => {
      if (type) {
        storage.setFilterParams(type, params)
        flushSync(() =>
          setFilterState({
            type,
            params,
          }),
        )
      }
    },
    [type, storage]
  )

  return {
    columnVisibility,
    setColumnVisibility: _setColumnVisibility,
    initialFilterParams:
      filterState.type === type
        ? filterState.params
        : type
        ? storage.getFilterParams(type)
        : null,
    saveFilterParams,
  }
}
